import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Animated as ReactAnimated,
  FlatList,
  Dimensions,
  ActivityIndicator,
  AppState,
} from 'react-native';
import Animated, {
  Easing,
  measure,
  runOnUI,
  useAnimatedRef,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { getBuildNumber, getBundleId, getUniqueId, getUserAgent, getDeviceId, getVersion, isTablet } from 'react-native-device-info';
import { EventRegister } from 'react-native-event-listeners';
import AppContext from '../../navigation/Context';
import colors from '../../resources/colors';
import images from '../../resources/images';
import { useToast } from 'react-native-toast-notifications';
import { format, isBefore, isValid } from 'date-fns';
import { useNavigation } from '@react-navigation/core';
import DataController from '../../controllers/DataController';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { createStackNavigator } from '@react-navigation/stack';
import Collapsible from 'react-native-collapsible';
import AuthenticationController from '../../controllers/AuthenticationController';
import HTML from 'react-native-render-html';
import IAPController from '../../controllers/IAPController';
import DatabaseService from '../../controllers/DatabaseController';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { RequestSubscriptionAndroid, RequestSubscriptionIOS } from 'react-native-iap';

const iOSURL = 'https://apps.apple.com/gb/app/lectionary/id583885035';
const androidURL = 'https://play.google.com/store/apps/details?id=com.aimer.lectionary';
const forgerPasswordURL = 'https://myaccount.hymnsam.co.uk/forgot-password/';
const contactURL = 'https://aimermedia.com/contact-aimer/';
const subscriptionURL = 'https://www.chpublishing.co.uk/apps/lectionary';

const appleSubscriptionsURL = 'https://apps.apple.com/account/subscriptions';
const googleSubscriptionsURL = 'https://play.google.com/store/account/subscriptions';

const privacyPolicy = 'https://www.chpublishing.co.uk/privacy';
const terms = 'https://www.chpublishing.co.uk/terms-and-conditions';

let checkAccessInterval;

// import s from 'react-native-web/dist/modules/AssetRegistry'

const AccountInfo = ({ user }) => {
  const logout = async () => {
    try {
      await AuthenticationController.logout();
      EventRegister.emit('auth/user-changed', null);
      EventRegister.emit('auth/logout');
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <View style={styles.webAccountCard}>
      <Text style={styles.dropdownText}>
        {'Account'}
      </Text>
      <Text style={[styles.subscriptionHeaderText, { marginTop: 12 }]}>
        {'Logged in as'}
      </Text>
      <Text style={[styles.subscriptionTextBold, { marginTop: 4 }]}>
        {user ? user.email : 'Default user'}
      </Text>
      <TouchableOpacity
        style={[styles.rightButton, { marginTop: 16, marginBottom: 24 }]}
        onPress={logout}>
        <Text style={styles.buttonText} allowFontScaling={false}>
          {'Log out'}
        </Text>
      </TouchableOpacity>
      <View style={styles.separator} />
    </View>
  )
};

const SubscriptionInfoNative = ({
  user,
  IAPSubscriptions,
  hymnsSubscription,
  subscriptions,
  appSubs,
  fontMultiplier,
}) => {
  const [appSubscriptions, setAppSubscriptions] = useState(appSubs);
  const [expiry, setExpiry] = useState(null);

  useEffect(() => {
    const checkIfActive = async () => {
      if (appSubs) {
        let expiryDate;

        if (Platform.OS === 'ios') {
          expiryDate = new Date(Number(appSubs.expires_date_ms));
        } else if (Platform.OS === 'android') {
          let expiry = await DataController.getExpiryDate();
          expiryDate = new Date(Number(expiry));
        }

        __DEV__ && console.log(expiryDate, new Date());
        setExpiry(expiryDate);
      }
    }

    checkIfActive();
  }, [appSubs]);

  useEffect(() => {
    setAppSubscriptions(appSubs);
  }, [appSubs]);

  const contactSupportPressed = () => {
    const sub = hymnsSubscription?.length > 0 && hymnsSubscription[0];
    const additionalInfo = sub && JSON.parse(sub.AdditionalAppData);

    const _appSubs = appSubs;

    try {
      delete _appSubs.transactionReceipt;
      delete _appSubs.signatureAndroid;
    } catch (error) {
      console.log('no app subs');
    }

    let body = `Type your query here

App Information:

App Identifier: ${getBundleId()}
App Version: ${getVersion()}
App Build: ${getBuildNumber()}
Platform: ${Platform.OS}
OS Version: ${Platform.Version}
Device Model: ${getDeviceId()}
Device UUID: ${getUniqueId()}

User Information:

HymnsEmail: ${user ? user.email : 'Unknown'}
HymnsToken: ${user ? user.token : 'Unknown'}
Offline Subscription: ${additionalInfo ? additionalInfo.bid.includes('offline') : 'Unknown'}
Subscription Start: ${sub ? sub.StartDate : 'Unknown'}
Subscription Expiry: ${sub ? sub.ExpiryDate : 'Unknown'}
Subscription ID: ${sub ? sub.SubscriptionId : 'Unknown'}
Subscription Product ID: ${additionalInfo ? additionalInfo.bid : 'Unknown'}
HymnsSub: ${hymnsSubscription ? JSON.stringify(hymnsSubscription, null, 2) : '[]'}
App Subscription: ${_appSubs ? JSON.stringify(_appSubs, null, 2) : ''}`;

    const url = `mailto:apps@hymnsam.co.uk?cc=apps@class.co.uk&subject=${encodeURI('Lectionary app support')}&body=${encodeURI(body)}`;

    DataController.openLink(url);
  };

  let latestSubscription = appSubscriptions || subscriptions?.length > 0 && subscriptions[0];
  let latestHymns = subscriptions?.length > 0 && subscriptions[0];

  const isHymns = latestHymns.ExpiryDate !== undefined;

  const currentSubIOS = Platform.OS === 'ios' && IAPSubscriptions.find((item) => appSubs && item.productId === appSubs.product_id);
  const currentSubAndroid = Platform.OS === 'android' && IAPSubscriptions.find((item) => appSubs && item.productId === appSubs.productId);

  return (
    <View style={styles.webAccountCard}>
      <Text style={styles.dropdownText}>
        {'Subscription info'}
      </Text>
      <Text style={[styles.subscriptionHeaderText, { marginTop: 12 }]}>
        {'Current subscription'}
      </Text>
      <Text style={[styles.subscriptionTextBold, { marginTop: 4 }]}>
        {latestSubscription ? isHymns ? latestHymns.Title : (Platform.OS === 'ios' ? currentSubIOS.title : currentSubAndroid.name) : 'Unknown'}
      </Text>
      <Text style={[styles.subscriptionHeaderText, { marginTop: 16 }]}>
        {'Expiry date'}
      </Text>
      <Text style={[styles.subscriptionTextBold, { marginTop: 4 }]}>
        {(expiry && isValid(expiry) && format(expiry, 'MMM d yyyy')) ||
          latestSubscription ? isHymns ? format(new Date(latestHymns.ExpiryDate), 'MMM d, yyyy') : format(new Date(Platform.OS === 'ios' ? Number(latestSubscription.expires_date_ms) : number(expiry)), 'MMM d, yyyy') : 'Unknown'}
      </Text>
      <Text style={[styles.subscriptionText, { marginTop: 32 }]}>
        {'Got a question or need help regarding subscriptions?'}
      </Text>
      <TouchableOpacity
        style={[styles.rightButton, {
          maxWidth: 364,
          width: '100%',
          marginTop: 12,
          marginBottom: 24,
          alignSelf: 'center',
        }]}
        onPress={contactSupportPressed}>
        <Text style={styles.buttonText} allowFontScaling={false}>
          {'Contact support'}
        </Text>
      </TouchableOpacity>
      <View style={styles.separator} />
    </View>
  )
};
const SubscriptionInfoWeb = ({
  user,
  subscriptions,
  hymnsSubscription,
  appSubs,
  fontMultiplier,
}) => {
  const [appSubscriptions, setAppSubscriptions] = useState(appSubs);

  useEffect(() => {
    setAppSubscriptions(appSubs);
  }, [appSubs]);

  const contactSupportPressed = async () => {
    const sub = hymnsSubscription?.length > 0 && hymnsSubscription[0];
    const additionalInfo = sub && JSON.parse(sub.AdditionalAppData);

    let body = `Type your query here

App Information:
  
Platform: ${Platform.OS}
OS Version: ${Platform.Version}
User Agent: ${await getUserAgent()}
App Version: 2.0.4
  
User Information:
  
HymnsEmail: ${user ? user.email : 'Unknown'}
HymnsToken: ${user ? user.token : 'Unknown'}
Offline Subscription: ${additionalInfo ? additionalInfo.bid.includes('offline') : 'Unknown'}
Subscription Start: ${sub ? sub.StartDate : 'Unknown'}
Subscription Expiry: ${sub ? sub.ExpiryDate : 'Unknown'}
Subscription ID: ${sub ? sub.SubscriptionId : 'Unknown'}
Subscription Product ID: ${additionalInfo ? additionalInfo.bid : 'Unknown'}
HymnsSub: ${hymnsSubscription ? JSON.stringify(hymnsSubscription, null, 2) : '[]'}
App Subscription: ${appSubscriptions ? JSON.stringify(appSubscriptions, null, 2) : '[]'}`;

    const url = `mailto:apps@hymnsam.co.uk?cc=apps@class.co.uk&subject=${encodeURI('Lectionary app support')}&body=${encodeURI(body)}`;

    __DEV__ && console.log(body);

    DataController.openLink(url);
  };

  let latestSubscription = subscriptions?.length > 0 && subscriptions[0];

  return (
    <View style={styles.webAccountCard}>
      <Text style={styles.dropdownText}>
        {'Subscription info'}
      </Text>
      <Text style={[styles.subscriptionHeaderText, { marginTop: 12 }]}>
        {'Current subscription'}
      </Text>
      <Text style={[styles.subscriptionTextBold, { marginTop: 4 }]}>
        {latestSubscription ? latestSubscription.Title : 'Unknown'}
      </Text>
      <Text style={[styles.subscriptionHeaderText, { marginTop: 16 }]}>
        {'Expiry date'}
      </Text>
      <Text style={[styles.subscriptionTextBold, { marginTop: 4 }]}>
        {latestSubscription ? format(new Date(latestSubscription.ExpiryDate), 'MMM d, yyyy') : 'Unknown'}
      </Text>
      <Text style={[styles.subscriptionText, { marginTop: 32 }]}>
        {'Got a question or need help regarding subscriptions?'}
      </Text>
      <TouchableOpacity
        style={[styles.rightButton, { width: 172, marginTop: 12, marginBottom: 24 }]}
        onPress={contactSupportPressed}>
        <Text style={styles.buttonText} allowFontScaling={false}>
          {'Contact support'}
        </Text>
      </TouchableOpacity>
      <View style={styles.separator} />
    </View>
  )
};

const SignInDropdown = ({ user }) => {
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const [open, setOpen] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordHidden, setPasswordHidden] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [errors, setErrors] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    EventRegister.addEventListener('auth/signing-in', (value) => {
      // if (signingIn !== value) {
      setSigningIn(value);
      // }
    });

    EventRegister.addEventListener('auth/sign-in-failed', (value) => {
      console.log(value);
      setErrors(value.state);
      setErrorMessage(value.error.message === 'Failed to fetch' ? 'Server error' : value.error);
      setSigningIn(false);
    });

    return () => {
      EventRegister.removeEventListener('auth/signing-in');
      EventRegister.removeEventListener('auth/sign-in-failed');
    }
  }, []);

  const getUserDataFromServer = async (user) => {
    await AuthenticationController.saveUser(user);

    const localBookmarks = await DataController.getBookmarks();
    const localNotes = await DataController.getAllNotes();
    const bookmarks = await DatabaseService.getAllBookmarks(user.email, user.requestToken);
    const notes = await DatabaseService.getAllNotes(user.email, user.requestToken);

    if (localBookmarks.length > 0) {
      if (bookmarks) {
        let allBookmarks = bookmarks;

        for (let i = 0; i < localBookmarks.length; i++) {
          if (!bookmarks.includes(localBookmarks[i])) {
            allBookmarks.push(localBookmarks[i]);
            await DatabaseService.updateBookmark(user.email, user.requestToken, localBookmarks[i]);
          }
        }

        await AsyncStorage.setItem('bookmarks', JSON.stringify(allBookmarks));
      } else {
        for (let i = 0; i < localBookmarks.length; i++) {
          await DatabaseService.updateBookmark(user.email, user.requestToken, localBookmarks[i]);
        }
      }

      EventRegister.emit('bookmarks/updated');
    } else if (bookmarks) {
      await AsyncStorage.setItem('bookmarks', JSON.stringify(bookmarks));
      EventRegister.emit('bookmarks/updated');
    }

    if (localNotes.length > 0) {
      if (notes) {
        let allNotes = notes;

        for (let i = 0; i < localNotes.length; i++) {
          let id = localNotes[i].id;

          if (notes.findIndex(item => item.id === id) === -1) {
            allNotes.push(localNotes[i]);

            let _note = { id: localNotes[i].id, note: encodeURI(localNotes[i].note) };
            await DatabaseService.updateNote(user.email, user.requestToken, _note);
          }
        }

        await AsyncStorage.setItem('notes', JSON.stringify(allNotes));
      } else {
        for (let i = 0; i < localNotes.length; i++) {
          let _note = { id: localNotes[i].id, note: encodeURI(localNotes[i].note) };
          await DatabaseService.updateNote(user.email, user.requestToken, _note);
        }
      }

      EventRegister.emit('notes/updated');
    } else if (notes) {
      await AsyncStorage.setItem('notes', JSON.stringify(notes));
      EventRegister.emit('notes/updated');
    }
  };

  const signIn = async () => {
    try {
      let user = await AuthenticationController.login(email, password);

      if (user) {
        await IAPController.updateHymnsSubscription(user);
        let subscriptions = await AuthenticationController.getSubscriptions(user.email, user.token, user.requestToken);

        if (subscriptions && subscriptions.length > 0) {
          subscriptions = subscriptions.filter((item) => item.Status === 1);
          subscriptions = subscriptions.sort((a, b) => new Date(b.ExpiryDate) - new Date(a.ExpiryDate));

          if (subscriptions.length > 0) {
            const latestSubscription = subscriptions[0];
            const isValid = isBefore(new Date(), new Date(latestSubscription.ExpiryDate));

            if (isValid) {
              await DataController.saveHymnsSubscription(subscriptions);
              await getUserDataFromServer(user);
              EventRegister.emit('auth/signing-in', false);
              EventRegister.emit('auth/user-changed', user);
            } else {
              await getUserDataFromServer(user);
              EventRegister.emit('auth/signing-in', false);
              EventRegister.emit('auth/user-changed', user);
            }
          } else {
            await getUserDataFromServer(user);
            EventRegister.emit('auth/signing-in', false);
            EventRegister.emit('auth/user-changed', user);
          }
        } else {
          await getUserDataFromServer(user);
          EventRegister.emit('auth/signing-in', false);
          EventRegister.emit('auth/user-changed', user);
        }
      } else {
        EventRegister.emit('auth/sign-in-failed', { state: true, error: 'Incorrect details' });
      }
    } catch (error) {
      console.log(error);
      EventRegister.emit('auth/sign-in-failed', { state: true, error });
    }
  };

  const handlePress = () => setOpen(!open);

  const emailChanged = (input) => {
    setEmail(input.trim());
    setErrors(false);
    setErrorMessage('');
  };

  const passwordChanged = (input) => {
    setPassword(input);
    setErrors(false);
    setErrorMessage('');
  };

  const inputFocused = () => {
    setErrors(false);
    setErrorMessage('');
  };

  const onEmailFocused = () => {
    setEmailFocused(true);
    setErrors(false);
    setErrorMessage('');
  };

  const emailBlurred = () => {
    setEmailFocused(false);
    setErrors(false);
    setErrorMessage('');
  };

  const onPasswordFocused = () => {
    setPasswordFocused(true);
    setErrors(false);
    setErrorMessage('');
  };

  const passwordBlurred = () => {
    setPasswordFocused(false);
    setErrors(false);
    setErrorMessage('');
  };

  const forgotPasswordPressed = () => DataController.openLink(forgerPasswordURL);

  const contactSupportPressed = () => DataController.openLink(contactURL);

  const togglePasswordVisibility = () => setPasswordHidden(!passwordHidden);

  const inputsEmpty = email === '' && password === '';

  const logout = async () => {
    try {
      await AuthenticationController.logout();
      EventRegister.emit('auth/user-changed', null);
      EventRegister.emit('auth/logout');
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <View>
      {user ? (
        <View style={styles.dropdownContent}>
          <View style={styles.webAccountCard}>
            <Text style={styles.dropdownText}>
              {'Account'}
            </Text>
            <Text style={[styles.subscriptionHeaderText, { marginTop: 12 }]}>
              {'Logged in as'}
            </Text>
            <Text style={[styles.subscriptionTextBold, { marginTop: 4 }]}>
              {user ? user.email : 'Default user'}
            </Text>
            <TouchableOpacity
              style={[styles.rightButton, { marginTop: 16, marginBottom: 24 }]}
              onPress={logout}>
              <Text style={styles.buttonText} allowFontScaling={false}>
                {'Log out'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : (
        <View>
          <TouchableOpacity
            style={styles.dropdown}
            activeOpacity={0.5}
            onPress={handlePress}>
            <Text style={styles.dropdownText} numberOfLines={1}>
              {user ? 'Account' : 'Sign in to hymnsam.co.uk'}
            </Text>
            <Image
              style={styles.dropdownIcon}
              source={open ? images.Button_Collapse_Red : images.Button_Expand_Red}
            />
          </TouchableOpacity>
          <Collapsible collapsed={!open}>
            <View style={styles.dropdownContent}>
              <Text style={styles.signInText}>
                {'Sign in to your hymnsam.co.uk account to restore an existing subscription or to share your subscription with other devices.'}
              </Text>
              <View>
                <Text
                  style={[styles.textInputHeader, { marginTop: 16 }]}
                  allowFontScaling={false}>
                  {'Email'}
                </Text>
                <TextInput
                  ref={emailRef}
                  style={styles.textInput}
                  editable={!signingIn}
                  numberOfLines={1}
                  placeholder={'joe.bloggs@example.co.uk'}
                  placeholderTextColor={colors.grey70}
                  value={email}
                  onChangeText={(input) => emailChanged(input)}
                  onFocus={onEmailFocused}
                  onBlur={emailBlurred}
                  autoCapitalize="none"
                  autoComplete="email"
                  keyboardType="email-address"
                  returnKeyType="next"
                  onSubmitEditing={() => passwordRef.current?.focus()}
                />
                <Text
                  style={[styles.textInputHeader, { marginTop: 12, }]}
                  allowFontScaling={false}>
                  {'Password'}
                </Text>
                <View>
                  <TextInput
                    ref={passwordRef}
                    style={[styles.textInput, { paddingEnd: 50 }]}
                    editable={!signingIn}
                    placeholder={'Password'}
                    placeholderTextColor={colors.grey70}
                    numberOfLines={1}
                    secureTextEntry={passwordHidden}
                    clearTextOnFocus={false}
                    value={password}
                    onChangeText={(input) => passwordChanged(input)}
                    onFocus={onPasswordFocused}
                    onBlur={passwordBlurred}
                    autoCapitalize="none"
                    autoComplete="password"
                    onSubmitEditing={() => {
                      if (!signingIn && !inputsEmpty) {
                        signIn();
                      }
                    }}
                  />
                  <TouchableOpacity
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                    }}
                    onPress={togglePasswordVisibility}>
                    <Image
                      style={styles.buttonMenu}
                      source={passwordHidden ? images.Button_View_Hide : images.Button_View_Show}
                    />
                  </TouchableOpacity>
                </View>
                {errors && (
                  <View style={{ marginTop: 12 }}>
                    <Text style={styles.errorMessage}>{errorMessage}</Text>
                    <Text style={styles.errorMessage}>
                    </Text>
                  </View>
                )}
                <TouchableOpacity
                  style={[styles.signInButton, {
                    opacity: inputsEmpty ? 0.4 : 1,
                    marginBottom: 24,
                  }]}
                  disabled={signingIn || inputsEmpty}
                  onPress={signIn}>
                  {signingIn ? (
                    <ActivityIndicator size="small" color={colors.accentWhite} style={styles.activityIndicator} />
                  ) :
                    (
                      <Text style={styles.signInButtonText}>
                        {'Sign in'}
                      </Text>
                    )}
                </TouchableOpacity>
              </View>
            </View>
          </Collapsible>
        </View>
      )}
      <View style={styles.separator} />
    </View>
  );
};

const SubscriptionOptions = ({
  IAPSubscriptions,
  user,
  hymnsSubscription,
  appSubs,
  fontMultiplier,
}) => {
  const appState = useRef(AppState.currentState);
  const context = useContext(AppContext);
  const trialData = context.trialData;

  const [open, setOpen] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [subscriptions, setSubscriptions] = useState(IAPSubscriptions);
  const [appSubscriptions, setAppSubscriptions] = useState(appSubs);
  const [isActive, setIsActive] = useState(false);
  const [expiry, setExpiry] = useState(null);

  useEffect(() => {
    setAppSubscriptions(appSubs);
  }, [appSubs]);

  useEffect(() => {
    setSubscriptions(IAPSubscriptions);
  }, [IAPSubscriptions]);

  useEffect(() => {
    const checkIfActive = async () => {
      if (appSubs) {
        let active = false;
        const date = new Date();
        let expiryDate;

        if (Platform.OS === 'ios') {
          expiryDate = new Date(Number(appSubs.expires_date_ms));
          active = expiryDate > date;
        } else if (Platform.OS === 'android') {
          let expiry = await DataController.getExpiryDate();
          expiryDate = new Date(Number(expiry));
          active = expiryDate > date;
        }

        __DEV__ && console.log(expiryDate, new Date());
        setExpiry(expiryDate);

        setIsActive(active);
        setAppSubscriptions(appSubs);
      }
    }

    checkIfActive();

    checkAccessInterval = setInterval(() => {
      checkIfActive();
    }, 10000);

    return () => clearInterval(checkAccessInterval);
  }, [appSubs]);

  useEffect(() => {
    EventRegister.addEventListener('subscriptions/restoring-purchases', (value) => setRestoring(value));

    return () => {
      EventRegister.removeEventListener('subscriptions/restoring-purchase');
    };
  }, []);

  const handlePress = () => setOpen(!open);

  const subscriptionButtonPressed = async (sku, offerToken, isAndroid) => {
    __DEV__ && console.log(sku, offerToken, isAndroid);
    let subscriptionRequest;

    if (isAndroid) {
      subscriptionRequest = { subscriptionOffers: [{ sku, offerToken }] };
    } else {
      subscriptionRequest = { sku };
    }

    await IAPController.buySubscription(subscriptionRequest, user);
  };

  const restorePressed = async () => {
    await IAPController.restorePurchases();
  };

  const manageSubscriptionPressed = () => {
    if (Platform.OS === 'ios') {
      DataController.openLink(appleSubscriptionsURL);
    } else if (Platform.OS === 'android') {
      DataController.openLink(googleSubscriptionsURL);
    }
  };

  const contactSupportPressed = () => {
    const sub = (hymnsSubscription && hymnsSubscription?.length > 0) && hymnsSubscription[0];
    const additionalInfo = sub && JSON.parse(sub.AdditionalAppData);

    const _appSubs = appSubs;

    try {
      delete _appSubs.transactionReceipt;
      delete _appSubs.signatureAndroid;
    } catch (error) {
      console.log('no app subs');
    }

    let body = `Type your query here

App Information:

App Identifier: ${getBundleId()}
App Version: ${getVersion()}
App Build: ${getBuildNumber()}
Platform: ${Platform.OS}
OS Version: ${Platform.Version}
Device Model: ${getDeviceId()}
Device UUID: ${getUniqueId()}

User Information:

HymnsEmail: ${user ? user.email : 'Unknown'}
HymnsToken: ${user ? user.token : 'Unknown'}
Offline Subscription: ${additionalInfo ? additionalInfo.bid.includes('offline') : 'Unknown'}
Subscription Start: ${sub ? sub.StartDate : 'Unknown'}
Subscription Expiry: ${sub ? sub.ExpiryDate : 'Unknown'}
Subscription ID: ${sub ? sub.SubscriptionId : 'Unknown'}
Subscription Product ID: ${additionalInfo ? additionalInfo.bid : 'Unknown'}
HymnsSub: ${hymnsSubscription ? JSON.stringify(hymnsSubscription, null, 2) : '[]'}
App Subscription: ${_appSubs ? JSON.stringify(_appSubs, null, 2) : '[]'}`;

    const url = `mailto:apps@hymnsam.co.uk?cc=apps@class.co.uk&subject=${encodeURI('Lectionary app support')}&body=${encodeURI(body)}`;

    try {
      __DEV__ && console.log(url);
      DataController.openLink(url);
    } catch (error) {
      console.log(error);
    }
  };

  const currentSubIOS = Platform.OS === 'ios' && IAPSubscriptions.find((item) => appSubscriptions && item.productId === appSubscriptions.product_id);
  const currentSubAndroid = Platform.OS === 'android' && IAPSubscriptions.find((item) => appSubscriptions && item.productId === appSubscriptions.productId);

  __DEV__ && console.log('appSubscription', appSubscriptions);
  __DEV__ && console.log('currentSubIOS', currentSubIOS);
  __DEV__ && console.log('currentSubAndroid', currentSubAndroid);
  __DEV__ && console.log('isActive', isActive);

  const trialActive = trialData.TrialEndDate && IAPController.checkTrialIsActive(new Date(trialData.TrialEndDate))

  return (
    <View>
      <TouchableOpacity
        style={styles.dropdown}
        activeOpacity={0.5}
        onPress={handlePress}>
        <Text style={styles.dropdownText}>
          {'Subscription options'}
        </Text>
        <Image
          style={styles.dropdownIcon}
          source={open ? images.Button_Collapse_Red : images.Button_Expand_Red}
        />
      </TouchableOpacity>
      <Collapsible collapsed={!open}>
        <View style={styles.dropdownContent}>
          {appSubscriptions && (currentSubIOS || currentSubAndroid) && isActive ? (
            <>
              <Text style={[styles.subscriptionHeaderText, { marginTop: 12 }]}>
                {'Current subscription'}
              </Text>
              <Text style={[styles.subscriptionTextBold, { marginTop: 4 }]}>
                {Platform.OS === 'ios' ? currentSubIOS.title : currentSubAndroid.name}
              </Text>
              <Text style={[styles.subscriptionHeaderText, { marginTop: 16 }]}>
                {'Expiry date'}
              </Text>
              <Text style={[styles.subscriptionTextBold, { marginTop: 4 }]}>
                {Platform.OS === 'ios'
                  ? format(new Date(Number(appSubscriptions.expires_date_ms)), 'MMM d, yyyy')
                  : format(new Date(Number(expiry)), 'MMM d, yyyy')
                }
              </Text>
              <TouchableOpacity
                style={[styles.rightButton, {
                  maxWidth: 364,
                  width: '100%',
                  marginTop: 24,
                  marginBottom: 24,
                  alignSelf: 'center',
                }]}
                onPress={manageSubscriptionPressed}>
                <Text style={styles.buttonText} allowFontScaling={false}>
                  {'Manage my subscription'}
                </Text>
              </TouchableOpacity>
            </>
          ) : (
            <>
              <Text style={styles.subscriptionText}>
                {trialActive
                  ? `You are currently using a free trial version of the app which expires on ${format(new Date(trialData.TrialEndDate), 'dd/MM/yyyy')}. If you would like to access Lectionary after this date, please select a subscription option below.`
                  : `Your free trial has expired on ${format(new Date(trialData.TrialEndDate), 'dd/MM/yyyy')}. If you would like to continue to access Lectionary, please select a subscription option below`}
              </Text>
              <View
                style={{
                  marginTop: 16,
                  flexDirection: Dimensions.get('window').width > 600 && Platform.OS !== 'android' ? 'row' : 'column',
                  maxWidth: 630,
                  alignSelf: Dimensions.get('window').width > 600 && Platform.OS !== 'android' ? 'center' : undefined,
                  marginBottom: Dimensions.get('window').width > 600 && Platform.OS !== 'android' ? 16 : 0,
                }}>
                {IAPSubscriptions.map((product, index) => {
                  if (Platform.OS === 'android') {
                    __DEV__ && console.log(product);
                    __DEV__ && console.log(product.subscriptionOfferDetails[0]?.pricingPhases);
                    __DEV__ && console.log(product.subscriptionOfferDetails[1]?.pricingPhases);

                    const hasTrial = product.subscriptionOfferDetails[0].pricingPhases.pricingPhaseList.length > 1;

                    const productToUse = hasTrial
                      ? product.subscriptionOfferDetails[0].pricingPhases.pricingPhaseList[1]
                      : product.subscriptionOfferDetails[0].pricingPhases.pricingPhaseList[0]

                    return (
                      <TouchableOpacity
                        key={index}
                        style={{
                          height: 80,
                          width: '100%',
                          maxWidth: 400,
                          borderRadius: 8,
                          backgroundColor: colors.primary,
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexDirection: 'column',
                          marginBottom: 16,
                        }}
                        onPress={() => subscriptionButtonPressed(product.productId, product.subscriptionOfferDetails[0]?.offerToken, true)}>
                        <Text
                          allowFontScaling={false}
                          style={{
                            fontFamily: 'FiraSans-SemiBold',
                            fontSize: 17,
                            fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
                            fontStyle: 'normal',
                            lineHeight: 24,
                            letterSpacing: 0.1,
                            color: colors.accentWhite,
                            textAlign: 'center',
                          }}>
                          {product.name}
                        </Text>
                        <Text
                          allowFontScaling={false}
                          style={{
                            fontFamily: 'FiraSans-SemiBold',
                            fontSize: 17,
                            fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
                            fontStyle: 'normal',
                            lineHeight: 24,
                            letterSpacing: 0.1,
                            color: colors.accentWhite,
                            marginTop: 8,
                          }}>
                          {/* {product?.localizedPrice} */}
                          {productToUse?.formattedPrice}
                        </Text>
                      </TouchableOpacity>
                    );
                  } else {
                    return (
                      <TouchableOpacity
                        key={product.productId}
                        style={{
                          height: Dimensions.get('window').width > 600 ? 132 : 50,
                          width: Dimensions.get('window').width > 600 ? 134 : '100%',
                          borderRadius: 8,
                          backgroundColor: colors.primary,
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexDirection: Dimensions.get('window').width > 600 ? 'column' : 'row',
                          marginBottom: Dimensions.get('window').width > 600 ? 0 : 16,
                          marginEnd: Dimensions.get('window').width > 600 && index !== IAPSubscriptions.length - 1 ? 24 : 0,
                        }}
                        onPress={() => subscriptionButtonPressed(product.productId, null, false)}>
                        <Text
                          allowFontScaling={false}
                          style={{
                            fontFamily: 'FiraSans-SemiBold',
                            fontSize: Dimensions.get('window').width > 600 ? 20 : 17,
                            fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
                            fontStyle: 'normal',
                            lineHeight: Dimensions.get('window').width > 600 ? 28 : 24,
                            letterSpacing: Dimensions.get('window').width > 600 ? -0.1 : 0.1,
                            color: colors.accentWhite,
                          }}>
                          {product.subscriptionPeriodNumberIOS === '1' ? '12 Months' : '3 Months'}
                          {Dimensions.get('window').width < 600 && ' '}
                        </Text>
                        <Text
                          allowFontScaling={false}
                          style={{
                            fontFamily: 'FiraSans-SemiBold',
                            fontSize: Dimensions.get('window').width > 600 ? 16 : 17,
                            fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
                            fontStyle: 'normal',
                            lineHeight: 24,
                            letterSpacing: Dimensions.get('window').width > 600 ? 0 : 0.1,
                            color: colors.accentWhite,
                            marginTop: Dimensions.get('window').width > 600 ? 2 : 0,
                            textAlign: 'center',
                          }}>
                          {product.title}{Dimensions.get('window').width < 600 && ' / '}
                        </Text>
                        <Text
                          allowFontScaling={false}
                          style={{
                            fontFamily: 'FiraSans-SemiBold',
                            fontSize: 17,
                            fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
                            fontStyle: 'normal',
                            lineHeight: 24,
                            letterSpacing: 0.1,
                            color: colors.accentWhite,
                            marginTop: Dimensions.get('window').width > 600 ? 8 : 0,
                          }}>
                          {product.localizedPrice}
                        </Text>
                      </TouchableOpacity>
                    )
                  }
                })}
              </View>
            </>
          )}
          <TouchableOpacity
            disabled={restoring}
            style={{
              height: 50,
              maxWidth: 364,
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: 12,
              alignSelf: 'center',
            }}
            onPress={restorePressed}>
            {restoring ? (
              <ActivityIndicator color={colors.primary} />
            ) : (
              <Text
                allowFontScaling={false}
                style={{
                  fontFamily: 'FiraSans-SemiBold',
                  fontSize: 17,
                  fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
                  fontStyle: 'normal',
                  lineHeight: 24,
                  letterSpacing: 0.1,
                  color: colors.primary,
                }}>
                {'Restore existing purchase'}
              </Text>
            )}
          </TouchableOpacity>
          <Text style={styles.subscriptionText}>
            {'Got a question or need help regarding subscriptions?'}
          </Text>
          <TouchableOpacity
            style={[styles.rightButton, {
              maxWidth: 364,
              width: '100%',
              marginTop: 12,
              marginBottom: 24,
              alignSelf: 'center',
            }]}
            onPress={contactSupportPressed}>
            <Text style={styles.buttonText} allowFontScaling={false}>
              {'Contact support'}
            </Text>
          </TouchableOpacity>
        </View>
      </Collapsible>
      <View style={styles.separator} />
    </View>
  );
};

const SubscriptionInfo = ({ user, fontMultiplier }) => {
  const [open, setOpen] = useState(true);

  const handlePress = () => setOpen(!open);

  const html = `
  <div>
    <ul>
      <li>In-app auto-renewing subscriptions: these are purchased using your ${Platform.OS === 'ios' ? 'Apple ID' : 'Google Play account'} and automatically renew. To cancel this type of subscription you need to visit your account on the ${Platform.OS === 'ios' ? `<a href="${appleSubscriptionsURL}">App Store</a>` : `<a href="${googleSubscriptionsURL}">Play Store</a>`}.</li>
      <li>If you already have a web subscription you can sign in using your account details above.</li>
      <li>Payment will be charged to your ${Platform.OS === 'ios' ? 'Apple ID' : 'Google Play account'} at confirmation of purchase.</li>
      <li>Subscription automatically renews unless auto-renew is turned off at least 24 hours before the end of the current subscription period.</li>
      <li>Subscriptions may be managed and auto-renewal may be turned off by going to your subscription settings in the ${Platform.OS === 'ios' ? `<a href="${appleSubscriptionsURL}">App Store</a>` : `<a href="${googleSubscriptionsURL}">Play Store</a>`} after purchase.</li>
      <li>Please read the <a href="${privacyPolicy}">Privacy Policy</a> and <a href="${terms}">Terms of Use</a></li>
      <li>Any unused portion of a free trial period, if offered, will be forfeited when the user purchases a subscription to that publication, where applicable.</li>
    </ul>
  </div>
  `;

  return (
    <View>
      <TouchableOpacity
        style={styles.dropdown}
        activeOpacity={0.5}
        onPress={handlePress}>
        <Text style={styles.dropdownText}>
          {'Subscription information'}
        </Text>
        <Image
          style={styles.dropdownIcon}
          source={open ? images.Button_Collapse_Red : images.Button_Expand_Red}
        />
      </TouchableOpacity>
      <Collapsible collapsed={!open}>
        <View style={styles.dropdownContent}>
          <HTML
            baseFontStyle={{
              fontFamily: 'FiraSans-Regular',
              fontSize: 17 * fontMultiplier,
              fontWeight: 'normal',
              fontStyle: 'normal',
              lineHeight: 24 * fontMultiplier,
              letterSpacing: -0.1,
              color: colors.defaultText,
            }}
            tagsStyles={{
              li: {
                fontFamily: 'FiraSans-Regular',
                fontSize: 17 * fontMultiplier,
                fontWeight: 'normal',
                fontStyle: 'normal',
                lineHeight: 24 * fontMultiplier,
                letterSpacing: -0.1,
                color: colors.defaultText,
              }
            }}
            source={{ html }}
            defaultTextProps={{ selectable: true }}
          />
        </View>
      </Collapsible>
      <View style={styles.separator} />
    </View>
  );
};

const SubscriptionsScreen = ({ navigation }) => {
  const toast = useToast();
  const context = useContext(AppContext);
  const [user, setUser] = useState(null);
  const [fontMultiplier, setFontMultiplier] = useState(1);
  const [validSubscription, setValidSubscription] = useState(true);
  const [validHymns, setValidHymns] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [appSubscriptions, setAppSubscriptions] = useState(null);
  const [IAPSubscriptions, setIAPSubscriptions] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [hymnsSubscription, setHymnsSubscription] = useState([]);
  const [expiryDate, setExpiryDate] = useState(0);

  const getFontMultiplier = async () => {
    const fontMultiplierInStorage = await DataController.getFontMultiplier();
    setFontMultiplier(fontMultiplierInStorage);
  };

  useEffect(() => {
    const getIAPs = async () => {
      let _subs = await IAPController.getSubscriptions();

      if (Platform.OS === 'android') {
        _subs = _subs.filter((product) => product.productId.includes('v02'));
      }
      setIAPSubscriptions(_subs);
    };

    if (Platform.OS !== 'web') {
      getIAPs();
    }
  }, []);

  useEffect(() => {
    getFontMultiplier();
  }, [])

  useEffect(() => {
    EventRegister.addEventListener('settings/font-size-updated', getFontMultiplier);
    EventRegister.addEventListener('auth/user-changed', async (value) => {
      setUser(value);
      const _subs = await DataController.getHymnsSubscription();
      setSubscriptions(_subs);
    });

    EventRegister.addEventListener('subscriptions/subscription-updated', async () => {
      const _subs = await DataController.getCurrentSubscription();
      setAppSubscriptions(_subs);
    });

    return () => {
      EventRegister.removeEventListener('settings/font-size-updated');
      EventRegister.removeEventListener('subscriptions/subscription-updated');
      EventRegister.removeEventListener('auth/user-changed');
    }
  }, []);

  useEffect(() => {
    const getUser = async () => {
      const _user = await AuthenticationController.getUser();
      __DEV__ && console.log('USER', _user);
      setUser(_user);
    };

    const getSubscriptions = async () => {
      const _subs = await DataController.getHymnsSubscription();
      const _appSubs = await DataController.getCurrentSubscription();
      __DEV__ && console.log('_appSubs', _appSubs);
      setSubscriptions(_subs);
      setAppSubscriptions(_appSubs);
    };

    getUser();
    getSubscriptions();
  }, []);

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <View style={styles.headerRow}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Image style={styles.menuIcon} source={images.Button_Back} />
            <Text style={styles.backButtonText} allowFontScaling={false}>
              {'Back'}
            </Text>
          </TouchableOpacity>
        </View>
      ),
      headerTitle: () => (
        <Text style={styles.headerTitle} allowFontScaling={false}>
          {Platform.OS === 'web' ? 'Account' : 'Subscriptions'}
        </Text>
      ),
      headerRight: () => <View />,
      headerTitleAlign: 'center',
      headerStyle: {
        backgroundColor: colors.grey20,
        shadowRadius: 0,
        shadowOffset: {
          height: 0,
        },
        borderBottomWidth: 0.5,
        borderBottomColor: colors.grey50,
        height: Platform.OS === 'web' ? 44 : undefined,
      },
    });
  }, [navigation,]);

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.scrollViewContainer}>
      {Platform.OS === 'web' ? (
        <>
          <AccountInfo
            user={user}
            fontMultiplier={fontMultiplier}
          />
          <SubscriptionInfoWeb
            user={user}
            subscriptions={subscriptions}
            hymnsSubscription={subscriptions}
            appSubs={appSubscriptions}
            fontMultiplier={fontMultiplier}
          />
        </>
      ) : (
        <>
          <SignInDropdown
            user={user}
            fontMultiplier={fontMultiplier}
          />
          {subscriptions && subscriptions.length > 0 ? (
            <SubscriptionInfoNative
              user={user}
              hymnsSubscription={subscriptions}
              subscriptions={subscriptions}
              appSubs={appSubscriptions}
              IAPSubscriptions={IAPSubscriptions}
              fontMultiplier={fontMultiplier}
            />
          ) : (
            <>
              <SubscriptionOptions
                IAPSubscriptions={IAPSubscriptions}
                hymnsSubscription={subscriptions}
                appSubs={appSubscriptions}
                user={user}
                fontMultiplier={fontMultiplier}
              />
              <SubscriptionInfo
                user={user}
                subscriptions={subscriptions}
                appSubs={appSubscriptions}
                fontMultiplier={fontMultiplier}
              />
            </>
          )}
        </>
      )
      }
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.accentWhite,
    flex: 1,
    // height: '100%',
  },
  backButton: {
    flexDirection: 'row',
    marginStart: 4,
    alignItems: 'center',
  },
  backButtonText: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 17,
    fontWeight: 'normal',
    color: colors.primary,
    marginStart: -5,
  },
  scrollViewContainer: {
    width: '100%',
    maxWidth: 1200,
    paddingHorizontal: Dimensions.get('window').width > 700 ? 30 : 15,
    paddingBottom: 54,
    alignSelf: 'center'
  },
  headerRow: {
    flexDirection: 'row',
  },
  buttonMenu: {
    width: 44,
    height: 44,
    marginStart: 4,
  },
  menuIcon: {
    width: 44,
    height: 44,
    tintColor: colors.primary,
  },
  headerTitle: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    textAlign: 'center',
    color: colors.primary,
  },
  dropdown: {
    height: 56,
    flexDirection: 'row',
    justifyContent: Dimensions.get('window').width > 700 ? 'flex-start' : 'space-between',
    alignItems: 'center',
    // marginStart: Dimensions.get('window').width > 700 ? 30 : 15,
    marginEnd: 10,
  },
  dropdownText: {
    fontFamily: 'FiraSans-Bold',
    fontSize: 20,
    fontWeight: Platform.OS === 'ios' ? 'bold' : 'normal',
    letterSpacing: 0.1,
    color: colors.primary,
    maxWidth: '85%'
  },
  dropdownIcon: {
    width: 44,
    height: 44,
  },
  dropdownContent: {
    // paddingHorizontal: Dimensions.get('window').width > 700 ? 30 : 15,
  },
  signInText: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 16,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    color: colors.defaultText,
  },
  textInputHeader: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 12,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: 16,
    color: colors.grey70,
    marginBottom: 4,
  },
  textInput: {
    height: 44,
    backgroundColor: colors.grey30,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: colors.grey40,
    borderRadius: 4,
    paddingHorizontal: 16,
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    letterSpacing: 0.1,
    color: colors.defaultText,
  },
  signInButton: {
    height: 44,
    width: 120,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 8,
    alignSelf: 'flex-end',
    marginTop: 16,
    marginStart: 16,
  },
  activityIndicator: {
    alignSelf: 'center',
  },
  signInButtonText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    letterSpacing: 0.1,
    textAlign: 'center',
    color: colors.accentWhite,
  },
  errorMessage: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 15,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 20,
    color: colors.primary,
  },
  subscriptionText: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 17,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: 26,
    letterSpacing: 0,
    color: colors.defaultText,
  },
  subscriptionButtonContainer: {

  },
  subscriptionButton: {

  },
  subscriptionButtonText: {

  },
  restoreButton: {

  },
  restoreButtonText: {

  },
  supportButton: {

  },
  webAccountCard: {
    paddingTop: 16,
  },
  subscriptionTextBold: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    lineHeight: 24,
    letterSpacing: 0.1,
    color: colors.defaultText,
  },
  subscriptionHeaderText: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 12,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: 16,
    letterSpacing: 0,
    color: colors.grey80,
  },
  separator: {
    height: 1,
    backgroundColor: colors.grey40,
  },
  rightButton: {
    width: 120,
    height: 44,
    borderRadius: 8,
    backgroundColor: colors.grey10,
    borderWidth: 1,
    borderColor: colors.primary,
    alignSelf: 'flex-end',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    lineHeight: 24,
    letterSpacing: 0.1,
    textAlign: 'center',
    color: colors.primary,
  },
});

export default SubscriptionsScreen;
